import { useCallback, useMemo, useRef, useState, type DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { FileCheck2, Loader2, ShieldCheck, Trash2, Upload, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@akuna/ui";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { useUserA1Certificate } from "@/hooks/useUserA1Certificate";
import { format } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";
import { cn } from "@/lib/utils";

const MAX_PFX_BYTES = 10 * 1024 * 1024;

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const buf = new Uint8Array(reader.result as ArrayBuffer);
      const chunk = 0x8000;
      let binary = "";
      for (let i = 0; i < buf.length; i += chunk) {
        binary += String.fromCharCode.apply(
          null,
          buf.subarray(i, i + chunk) as unknown as number[],
        );
      }
      resolve(btoa(binary));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

type ValidateResponse = { valid: true } | { valid: false; error?: string };
type SuccessResponse = { success: true };
type ErrorResponse = { error: string };

async function getFunctionErrorResponse(error: unknown): Promise<ErrorResponse | null> {
  const context = (error as { context?: unknown } | null)?.context;
  if (!(context instanceof Response)) {
    return null;
  }

  try {
    const body = (await context.clone().json()) as Partial<ErrorResponse>;
    return typeof body.error === "string" ? { error: body.error } : null;
  } catch {
    return null;
  }
}

export function CertificateTab() {
  const { t, i18n } = useTranslation("settings", { keyPrefix: "certificate" });
  const queryClient = useQueryClient();
  const { data: certificate, isLoading } = useUserA1Certificate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState("");
  const [validationState, setValidationState] = useState<"idle" | "valid" | "invalid">("idle");
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const dateLocale = useMemo(() => (i18n.language.startsWith("pt") ? ptBR : enUS), [i18n.language]);

  const resetForm = useCallback(() => {
    setFile(null);
    setPassword("");
    setValidationState("idle");
    setInlineError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const invoke = async (body: Record<string, string>) => {
    const { data, error } = await supabase.functions.invoke<
      ValidateResponse | SuccessResponse | ErrorResponse
    >("a1-certificate-manager", { body });

    if (error) {
      const errorResponse = await getFunctionErrorResponse(error);
      if (errorResponse) {
        return errorResponse;
      }
      throw new Error(error.message);
    }
    if (data && typeof data === "object" && "error" in data && (data as ErrorResponse).error) {
      return data as ErrorResponse;
    }
    return data;
  };

  const validateInputs = useCallback(() => {
    if (!file) {
      return t("inlineErrors.missingFile");
    }
    if (!password) {
      return t("inlineErrors.missingPassword");
    }
    if (!file.name.toLowerCase().endsWith(".pfx")) {
      return t("inlineErrors.invalidExtension");
    }
    if (file.size > MAX_PFX_BYTES) {
      return t("inlineErrors.fileTooLarge");
    }
    return null;
  }, [file, password, t]);

  const handleSave = async () => {
    const inputError = validateInputs();
    if (inputError) {
      setValidationState("invalid");
      setInlineError(inputError);
      toast.error(inputError);
      return;
    }

    setUploading(true);
    setValidationState("idle");
    setInlineError(null);
    try {
      const selectedFile = file as File;
      const pfxBase64 = await fileToBase64(selectedFile);
      const validationRes = await invoke({ action: "validate", pfxBase64, password });

      if (validationRes && "error" in validationRes && validationRes.error) {
        const message = t(`errors.${validationRes.error}`, { defaultValue: validationRes.error });
        setValidationState("invalid");
        setInlineError(message);
        toast.error(message);
        return;
      }

      if (!validationRes || !("valid" in validationRes) || !validationRes.valid) {
        setValidationState("invalid");
        setInlineError(t("inlineErrors.invalidCertificate"));
        toast.error(t("toastInvalidCertificate"));
        return;
      }

      const uploadRes = await invoke({
        action: "upload",
        pfxBase64,
        password,
        filename: selectedFile.name,
      });

      if (uploadRes && "error" in uploadRes && uploadRes.error) {
        toast.error(t(`errors.${uploadRes.error}`, { defaultValue: uploadRes.error }));
        return;
      }

      setValidationState("valid");
      toast.success(t("toastUploaded"));
      resetForm();
      await queryClient.invalidateQueries({ queryKey: ["user-a1-certificate"] });
    } catch (e) {
      console.error(e);
      setValidationState("invalid");
      setInlineError(t("inlineErrors.validateError"));
      toast.error(e instanceof Error ? e.message : t("toastUploadError"));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await invoke({ action: "delete" });

      if (res && "error" in res && res.error) {
        toast.error(t(`errors.${res.error}`, { defaultValue: res.error }));
        return;
      }

      toast.success(t("toastDeleted"));
      setDeleteOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["user-a1-certificate"] });
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : t("toastDeleteError"));
    } finally {
      setDeleting(false);
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    setFile(f ?? null);
    setValidationState("idle");
    setInlineError(null);
  };

  const clearSelectedFile = () => {
    setFile(null);
    setValidationState("idle");
    setInlineError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDragState = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    handleDragState(event);
    if (uploading) return;
    setIsDragging(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    handleDragState(event);

    if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
      return;
    }

    setIsDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    handleDragState(event);
    setIsDragging(false);

    if (uploading) return;

    const droppedFile = event.dataTransfer.files?.[0];
    if (!droppedFile) return;

    setFile(droppedFile);
    setValidationState("idle");
    setInlineError(null);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const hasCert = Boolean(certificate);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            {t("title")}
          </CardTitle>
          <CardDescription>{t("description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {hasCert ? (
            <div className="rounded-xl border bg-muted/30 p-4 sm:p-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-foreground">{t("statusLabel")}</span>
                    <Badge variant="secondary" className="rounded-full px-3 py-1">
                      {t("statusUploaded")}
                    </Badge>
                  </div>

                  <div className="space-y-1 text-sm">
                    {certificate?.original_filename ? (
                      <p>
                        <span className="text-muted-foreground">{t("fileLabel")} </span>
                        <span className="font-medium text-foreground">{certificate.original_filename}</span>
                      </p>
                    ) : null}
                    {certificate?.uploaded_at ? (
                      <p>
                        <span className="text-muted-foreground">{t("uploadedAtLabel")} </span>
                        <span className="text-foreground">
                          {format(new Date(certificate.uploaded_at), "PPpp", { locale: dateLocale })}
                        </span>
                      </p>
                    ) : null}
                  </div>
                </div>

                <Button type="button" variant="destructive" onClick={() => setDeleteOpen(true)}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t("delete")}
                </Button>
              </div>
            </div>
          ) : null}

          {!hasCert ? (
            <div className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="a1-cert-file">{t("pfxLabel")}</Label>
                <div
                  className={cn(
                    "rounded-xl border border-dashed border-border bg-muted/20 p-4 transition-colors sm:p-5",
                    isDragging && "border-primary bg-primary/5",
                  )}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragState}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    id="a1-cert-file"
                    type="file"
                    accept=".pfx,application/x-pkcs12"
                    onChange={onFileChange}
                    disabled={uploading}
                    className="hidden"
                  />

                  {file ? (
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex min-w-0 items-start gap-3">
                        <div className="rounded-full bg-primary/10 p-2 text-primary">
                          <FileCheck2 className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-foreground">{file.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {`${(file.size / (1024 * 1024)).toFixed(2)} MB`}
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading}
                        >
                          {t("changeFile")}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={clearSelectedFile}
                          disabled={uploading}
                        >
                          <X className="mr-2 h-4 w-4" />
                          {t("clearFile")}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center text-center">
                      <div className="mb-3 rounded-full bg-primary/10 p-3 text-primary">
                        <Upload className="h-5 w-5" />
                      </div>
                      <p className="text-sm font-medium text-foreground">{t("uploadTitle")}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{t("pfxHint")}</p>
                      <Button
                        type="button"
                        variant="outline"
                        className="mt-4"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        {t("selectFile")}
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="a1-cert-password">{t("passwordLabel")}</Label>
                <Input
                  id="a1-cert-password"
                  type="password"
                  autoComplete="off"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setValidationState("idle");
                    setInlineError(null);
                  }}
                  disabled={uploading}
                  placeholder={t("passwordPlaceholder")}
                />
              </div>

              <div className="flex flex-wrap items-center gap-2 min-h-6">
                {validationState === "valid" ? (
                  <Badge variant="default" className="bg-green-600 hover:bg-green-600">
                    {t("badgeValid")}
                  </Badge>
                ) : null}
                {validationState === "invalid" ? <Badge variant="destructive">{t("badgeInvalid")}</Badge> : null}
              </div>

              {inlineError ? <p className="text-sm text-destructive">{inlineError}</p> : null}

              <div className="space-y-2">
                <Button type="button" onClick={handleSave} disabled={uploading} className="w-full sm:w-auto">
                  {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                  {t("save")}
                </Button>
                <p className="text-xs text-muted-foreground">{t("saveHint")}</p>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("deleteDescription")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t("deleteCancel")}</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              disabled={deleting}
              onClick={() => void handleDelete()}
            >
              {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t("deleteConfirm")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
